import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../FreeAssesment/loader.css";
import logo from "../../assets/ExamWarriorLogo.svg";
import msgIcon from "../../assets/msgicon.svg"

function TopicTestPage() {
    const { topic } = useParams();
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});
    const [showPopup, setShowPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [testType, setTestType] = useState(null);
    const [hasBadge, setHasBadge] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [answerStatus, setAnswerStatus] = useState({}); // Track answer status for immediate feedback
    const navigate = useNavigate();
    const isResizing = useRef(false);
    const startX = useRef(0);

    const user = JSON.parse(localStorage.getItem('user'));
    const childId = user?.id || "";

    useEffect(() => {
        const checkBadge = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/check-badge`,
                    { params: { childId, category: topic } }
                );
                setHasBadge(response.data.hasBadge);
            } catch (error) {
                console.error("Error checking badge:", error);
            }
        };

        checkBadge();
    }, [topic, childId]);

    useEffect(() => {
        if (testType && questions.length > 0) {
            window.history.pushState(null, null, window.location.pathname);
            const preventBackNavigation = () => {
                window.history.pushState(null, null, window.location.pathname);
                alert("Back navigation is disabled during the test.");
            };

            window.addEventListener("popstate", preventBackNavigation);
            return () => window.removeEventListener("popstate", preventBackNavigation);
        }
    }, [testType, questions]);

    const handleButtonClick = () => {
        setShowPopup(true);
    };

    const handleCancel = () => {
        setShowPopup(false);
    };

    const handleConfirm = () => {
        setShowPopup(false);
        handleSubmit();
    };

    const [hasCompletedSubjectTest, setHasCompletedSubjectTest] = useState(false);
    const [checkingCompletion, setCheckingCompletion] = useState(false);

    // Check if student has completed subject test with 100%
    const checkSubjectTestCompletion = async () => {
        setCheckingCompletion(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/check-subject-test-completion`,
                { params: { childId, category: topic } }
            );
            setHasCompletedSubjectTest(response.data.hasCompleted);
        } catch (error) {
            console.error("Error checking subject test completion:", error);
            toast.error("Failed to check subject test completion status");
        } finally {
            setCheckingCompletion(false);
        }
    };

    // Call this function when component mounts
    useEffect(() => {
        if (childId && topic) {
            checkSubjectTestCompletion();
        }
    }, [childId, topic]);

    const fetchQuestions = async (type) => {
        if (type === "badge" && hasBadge) {
            toast.info("You already have a badge for this topic!", { autoClose: 3000 });
            return;
        }

        setTestType(type);
        setLoading(true);
        setAnswers({});
        setAnswerStatus({});

        try {
            const userData = localStorage.getItem("user");
            const user = JSON.parse(userData);
            const questionType = user.is_oc ? "OC" : "Selective";
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/get-topic-questions`,
                { params: { category: topic, testType: type, questionType } }
            );

            setQuestions(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching questions:", error);
            toast.error("Failed to load questions. Please try again.");
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const responses = questions.map(q => ({
                questionId: q.id,
                selectedAnswer: answers[q.id] || ""
            }));

            const payload = {
                childId,
                category: topic,
                testType,
                responses
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/topic-tests/submit-topic-test`,
                payload
            );

            if (response.data.success) {
                const testId = response.data.testId;

                if (testType === "badge" && response.data.earnedBadge) {
                    toast.success(
                        `Congratulations! You earned a badge for ${topic} with a perfect score!`,
                        { autoClose: 3000 }
                    );
                } else if (testType === "badge") {
                    toast.info(
                        `You scored ${response.data.score}/${response.data.totalQuestions}. ` +
                        `Try again to get all answers correct and earn a badge!`,
                        { autoClose: 3000 }
                    );
                } else {
                    toast.success(
                        `Test submitted! Score: ${response.data.score}/${response.data.totalQuestions}`,
                        { autoClose: 3000 }
                    );
                }

                // For subject tests, don't redirect to report page
                if (testType === "badge") {
                    setTimeout(() => {
                        navigate(`/test-results/topic/${testId}`);
                    }, 3000);
                } else {
                    setTimeout(() => {
                        navigate('/maths-topics');
                    }, 3000);
                }
            }
        } catch (error) {
            toast.error("Error submitting test.");
            console.error("Submission Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced answer comparison (same as backend logic)
    // Enhanced answer comparison (same as backend logic)
    const isAnswerCorrect = (selected, correct, options) => {
        if (!selected || !correct) return false;

        // Clean both answers
        const cleanSelected = selected.toString().trim().toUpperCase();
        const cleanCorrect = correct.toString().trim().toUpperCase();

        // Direct match (most common case)
        if (cleanSelected === cleanCorrect) return true;

        // If options are provided, check if correct answer is a letter that matches an option
        if (options) {
            // Case 1: Correct answer is a letter (A, B, C...)
            if (/^[A-E]$/.test(cleanCorrect)) {
                const correctOption = options.find(opt => opt.letter === cleanCorrect);
                if (correctOption) {
                    // Check if selected answer matches the option text
                    const optionText = correctOption.text.toUpperCase();
                    if (cleanSelected === optionText || cleanSelected.includes(optionText)) {
                        return true;
                    }
                }
            }

            // Case 2: Selected answer is a letter (A, B, C...)
            if (/^[A-E]$/.test(cleanSelected)) {
                const selectedOption = options.find(opt => opt.letter === cleanSelected);
                if (selectedOption && cleanCorrect === selectedOption.text.toUpperCase()) {
                    return true;
                }
            }

            // Case 3: Check if selected answer matches any option text
            const matchingOption = options.find(opt => {
                const optionText = opt.text.toUpperCase();
                return cleanSelected === optionText || cleanSelected.includes(optionText);
            });

            if (matchingOption) {
                // If correct answer is a letter, check if it matches this option's letter
                if (/^[A-E]$/.test(cleanCorrect) && cleanCorrect === matchingOption.letter) {
                    return true;
                }
                // If correct answer is text, check if it matches this option's text
                if (cleanCorrect === matchingOption.text.toUpperCase()) {
                    return true;
                }
            }

            // Case 4: Check if correct answer matches any option text
            const correctMatchingOption = options.find(opt =>
                opt.text.toUpperCase() === cleanCorrect
            );

            if (correctMatchingOption) {
                // Check if selected answer matches the option letter
                if (cleanSelected === correctMatchingOption.letter) {
                    return true;
                }
                // Check if selected answer matches the option text
                if (cleanSelected === correctMatchingOption.text.toUpperCase()) {
                    return true;
                }
            }
        }

        // Handle partial matches for text answers
        if (cleanSelected.includes(cleanCorrect) || cleanCorrect.includes(cleanSelected)) {
            // But be more strict - only allow if it's a significant match
            const selectedWords = cleanSelected.split(/\s+/);
            const correctWords = cleanCorrect.split(/\s+/);

            // Count matching words
            const matchingWords = selectedWords.filter(word =>
                correctWords.includes(word) && word.length > 2 // Only count words longer than 2 characters
            );

            // Require at least 60% of words to match
            const similarity = matchingWords.length / Math.max(selectedWords.length, correctWords.length);
            if (similarity >= 0.6) {
                return true;
            }
        }

        return false;
    };

    const handleSelectAnswer = (questionId, selectedAnswer) => {
        const currentQuestion = questions.find(q => q.id === questionId);
        if (!currentQuestion) return;

        const mcqOptions = currentQuestion.mcq_options ? parseMcqOptions(currentQuestion.mcq_options) : [];
        const isCorrect = isAnswerCorrect(selectedAnswer, currentQuestion.correct_answer, mcqOptions);

        setAnswers(prev => ({
            ...prev,
            [questionId]: selectedAnswer
        }));

        if (testType === "subject") {
            setShowExplanation(true);
        }

        // For subject tests, show immediate feedback
        if (testType === "subject") {
            setAnswerStatus(prev => ({
                ...prev,
                [questionId]: isCorrect ? "correct" : "incorrect"
            }));
        }
    };
    useEffect(() => {
        // Hide explanation when changing questions
        setShowExplanation(false);
    }, [currentIndex]);

    const parseMcqOptions = (optionsString) => {
        if (!optionsString) return [];

        const options = [];
        // First split by commas that are followed by space and capital letter (option separator)
        const parts = optionsString.split(/,\s(?=[A-Z]\s)/);

        parts.forEach(part => {
            if (part.trim()) {
                const spaceIndex = part.indexOf(' ');
                if (spaceIndex > 0) {
                    const letter = part.substring(0, spaceIndex).trim();
                    let text = part.substring(spaceIndex + 1).trim();

                    // Special handling for option E to preserve its exact text
                    if (letter === 'E') {
                        // Keep the text exactly as is, including any commas
                    }
                    // For other options, remove trailing comma if it exists
                    else if (text.endsWith(',')) {
                        text = text.slice(0, -1).trim();
                    }

                    if (letter && text) {
                        options.push({ letter, text });
                    }
                }
            }
        });

        return options;
    };

    const handleMouseDown = (e) => {
        isResizing.current = true;
        startX.current = e.clientX;
        document.body.style.cursor = "col-resize";
    };

    const handleBackToTopics = () => {
        navigate('/maths-topics', { replace: true });
    };

    if (!testType) {
        return (
            <div className="h-screen bg-white flex flex-col items-center justify-center">
                <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-lg">
                    <h2 className="text-2xl font-bold text-center mb-8">
                        Test Options for: <span className="text-orange-500">{topic}</span>
                    </h2>

                    {hasBadge && (
                        <div className="mb-6 p-4 bg-green-100 border border-green-400 rounded-lg">
                            <p className="text-green-800 text-center">
                                🎉 You already have a badge for this topic! 🎉
                            </p>
                        </div>
                    )}

                    <div className="space-y-6">
                        <button
                            onClick={() => fetchQuestions("subject")}
                            className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-bold text-xl rounded-lg shadow-md transition-all duration-200"
                        >
                            Practice questions
                            <p className="text-sm font-normal mt-1">
                                Practice Maths questions on each topic and do a test to earn the mastery and become a warrior.
                            </p>
                        </button>

                        <button
                            onClick={() => {
                                if (!hasCompletedSubjectTest) {
                                    toast.info("You need to complete the subject test with a perfect score first!");
                                    return;
                                }
                                fetchQuestions("badge");
                            }}
                            className={`w-full py-4 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xl rounded-lg shadow-md transition-all duration-200 ${hasBadge || !hasCompletedSubjectTest
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:from-purple-600 hover:to-pink-600"
                                }`}
                            disabled={hasBadge || !hasCompletedSubjectTest}
                        >
                            Ninja Mastery
                            <p className="text-sm font-normal mt-1">
                                {hasBadge
                                    ? "You already earned a badge for this topic!"
                                    : !hasCompletedSubjectTest
                                        ? "Complete subject test with 100% to unlock!"
                                        : "Test-type questions. Get all correct to earn a badge!"}
                            </p>
                        </button>

                        <button
                            onClick={handleBackToTopics}
                            className="w-full py-2 px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg shadow-md transition duration-200"
                        >
                            Back to Topics
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading || questions.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
                <div className="loadingspinner">
                    <div id="square1" className="bg-orange-500"></div>
                    <div id="square2" className="bg-orange-400"></div>
                    <div id="square3" className="bg-orange-300"></div>
                    <div id="square4" className="bg-yellow-500"></div>
                    <div id="square5" className="bg-yellow-400"></div>
                </div>
            </div>
        );
    }

    const totalQuestions = questions.length;
    const currentQuestion = questions[currentIndex];
    const mcqOptions = currentQuestion?.mcq_options ? parseMcqOptions(currentQuestion.mcq_options) : [];
    const currentAnswerStatus = answerStatus[currentQuestion.id];

    return (
        <div className="h-screen bg-white flex flex-col">
            {/* Header */}
            <div className="absolute w-full h-16 sm:h-24 flex justify-between items-center py-1 px-3 bg-white shadow-md">
                <img src={logo} alt="logo" className="h-14 sm:h-24 w-20 sm:w-32" />
                <div className="text-center">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 
                   text-lg sm:text-2xl font-extrabold tracking-wide drop-shadow-md">
                        {testType === "subject" ? "Practice" : "Ninja Mastery"}: {topic}
                    </span>
                </div>

            </div>

            {/* Main Content */}
            <div className="flex-grow flex flex-col md:flex-row pt-16 sm:pt-20 overflow-hidden">
                {/* Question Panel */}
                <div className="w-full md:w-1/2 bg-white p-3 sm:p-6 overflow-y-auto max-h-[calc(100vh-120px)] mt-6 rounded-xl ">
                    <p className="mb-3 sm:mb-4">
                        <span className="block text-orange-500 bg-clip-text text-xl sm:text-2xl font-extrabold tracking-wider uppercase mb-3 drop-shadow-md border-b-2 border-purple-300 pb-1">
                            Question {currentIndex + 1} of {totalQuestions}
                        </span>


                        <span className="text-xl sm:text-2xl font-bold text-gray-900 leading-relaxed">
                            {currentQuestion?.question || "No question available"}
                        </span>
                    </p>

                    {currentQuestion?.image_data && (
                        <div className="mt-4">
                            <img
                                src={
                                    currentQuestion.image_data.startsWith("http")
                                        ? currentQuestion.image_data
                                        : `http://${currentQuestion.image_data}`
                                }
                                alt="Question Image"
                                className="max-w-full rounded-lg shadow-lg"
                            />
                        </div>
                    )}
                    {/* Explanation Section */}
                    {testType === "subject" && showExplanation && currentQuestion?.explanation && (
                        <div className="mt-10 flex items-start">
                            {/* Explanation Box */}
                            <div className="relative p-4 bg-blue-50 border border-blue-200 rounded-lg w-[600px]">
                                <h3 className="text-lg font-semibold text-blue-800 mb-2">Explanation:
                                <span className="text-gray-700"> {currentQuestion.explanation}</span></h3>
                            </div>

                            {/* Message Icon outside box */}
                            <img
                                src={msgIcon}
                                alt="Message Icon"
                                className="-ml-8 mt-8 w-[140px] h-[150px]"
                            />

                        </div>
                    )}


                </div>


                {/* Resize Handle (Desktop only) */}
                <div
                    className="resize-handle hidden md:block"
                    onMouseDown={handleMouseDown}
                    style={{
                        cursor: "col-resize",
                        backgroundColor: "gray",
                        width: "3px",
                        height: "auto",
                        marginLeft: "8px",
                        marginRight: "8px",
                    }}
                ></div>

                {/* Options Panel */}
                <div className="w-full md:w-1/2 bg-white pb-20 md:pb-32 p-3 sm:p-6">
                    <div className="space-y-3 sm:space-y-6 mt-12 sm:mt-16">
                        {mcqOptions.map((option, index) => {
                            const displayText = option.text.endsWith(',')
                                ? option.text.slice(0, -1).trim()
                                : option.text.trim();
                            const fullOption = `${option.letter}) ${displayText}`;

                            // Determine if this option is selected
                            const isSelected = answers[currentQuestion.id] === fullOption;

                            // Determine styling based on test type and answer status
                            let optionStyle = "flex items-center space-x-2 py-2 sm:py-4 px-2 bg-gray-200 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors";

                            if (testType === "subject" && isSelected) {
                                if (currentAnswerStatus === "correct") {
                                    optionStyle = "flex items-center space-x-2 py-2 sm:py-4 px-2 bg-green-100 border border-green-400 rounded-lg transition-colors";
                                } else if (currentAnswerStatus === "incorrect") {
                                    optionStyle = "flex items-center space-x-2 py-2 sm:py-4 px-2 bg-red-100 border border-red-400 rounded-lg transition-colors";
                                }
                            }

                            return (
                                <label key={index} className={optionStyle}>
                                    <input
                                        type="radio"
                                        name={`question-${currentQuestion.id}`}
                                        value={fullOption}
                                        className="peer h-6 sm:h-8 w-6 sm:w-8"
                                        onChange={() => handleSelectAnswer(currentQuestion.id, fullOption)}
                                        checked={isSelected}
                                    />
                                    <span className="text-black text-base sm:text-lg font-semibold">{displayText}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Navigation Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-orange-500 text-white py-2 sm:py-3 flex justify-between">
                <button
                    onClick={() => setCurrentIndex((prev) => Math.max(prev - 1, 0))}
                    className={`p-1 sm:p-2 px-3 sm:px-4 rounded-md font-semibold bg-black ml-2 ${currentIndex === 0 ? "opacity-50" : ""}`}
                    disabled={currentIndex === 0}
                >
                    &lt;&lt; PREV
                </button>

                {currentIndex < totalQuestions - 1 ? (
                    <button
                        onClick={() => setCurrentIndex((prev) => prev + 1)}
                        className="p-1 sm:p-2 px-3 sm:px-4 rounded-md font-semibold bg-black mr-2 sm:mr-3"
                    >
                        NEXT &gt;&gt;
                    </button>
                ) : (
                    <button
                        onClick={handleButtonClick}
                        className="bg-black text-white font-bold p-1 sm:p-2 px-4 sm:px-6 mr-2 sm:mr-3 rounded-md text-sm sm:text-base"
                    >
                        {testType === "subject" ? "Submit Test" : "Submit Challenge"}
                    </button>
                )}
            </div>

            {/* Submission Confirmation Popup */}
            {showPopup && (
                <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-lg shadow-lg overflow-hidden transform transition-all w-full max-w-xs sm:max-w-md mx-4 sm:mx-auto">
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="absolute top-2 sm:top-4 right-2 sm:right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="px-4 sm:px-6 py-4 sm:py-8">
                            <div className="flex items-center space-x-3 sm:space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="h-10 sm:h-12 w-10 sm:w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <svg className="h-5 sm:h-6 w-5 sm:w-6 text-green-600" xmlns="http://www.w3.org2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                    Confirm Submission
                                </h3>
                            </div>

                            <div className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-700">
                                Are you sure you want to submit the {testType === "subject" ? "subject test" : "badge challenge"}?
                                {testType === "badge" && !hasBadge && (
                                    <p className="mt-1 sm:mt-2 font-semibold">
                                        Remember: You need all answers correct to earn a badge!
                                    </p>
                                )}
                            </div>

                            <div className="mt-4 sm:mt-6 flex justify-end space-x-2 sm:space-x-3">
                                <button
                                    onClick={handleCancel}
                                    type="button"
                                    className="px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    type="button"
                                    className="px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Indicator */}
            {isLoading && (
                <div className="fixed inset-0 flex flex-col sm:flex-row justify-center items-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="animate-spin rounded-full h-12 sm:h-16 w-12 sm:w-16 border-t-4 border-blue-500"></div>
                    <span className="mt-2 sm:mt-0 sm:ml-4 text-white text-sm sm:text-base">
                        {testType === "subject" ? "Submitting your test..." : "Submitting your test and sending report..."}
                    </span>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}

export default TopicTestPage;